/**
 * O bloco "Ações" da coluna ao lado da grade.
 *
 * Papel: desenha "🖨 Imprimir / PDF", que pede a folha impressa do mês,
 * "Copiar da semana anterior", que abre a caixa da cópia, "🔍 Verificar
 * conflitos do mês", que abre e fecha logo abaixo dele a lista de pendências de
 * cada dia, ou a frase de que o mês não tem nenhuma, e "Limpar mês inteiro".
 *
 * Como usar: imprimir e verificar são leitura e aparecem em qualquer nível de
 * acesso; copiar e limpar gravam, e só aparecem com `podeEditar`. Os dois só
 * chamam quem monta o bloco, que é quem abre a caixa da cópia e pede a
 * confirmação da limpeza.
 * `pendencias` chega pronto de `pendenciasDoMes`, e a lista acompanha a escala
 * enquanto está aberta: um dia corrigido na caixa do dia sai da lista assim que
 * a gravação volta, sem precisar fechar e abrir de novo.
 *
 * O botão de imprimir some da própria impressão, junto com a barra do mês: num
 * Ctrl+P dado na tela, sem a folha montada, os dois não iriam para o papel.
 */
import { useState } from 'react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { type PendenciasDoDia, fraseDaPendencia } from '../escala';
import { diaPorExtenso } from '../grade';
import { SecaoDoPainel } from './partes-do-painel';

interface AcoesDoMesProps {
  /** As pendências do mês mostrado, dia a dia. */
  pendencias: PendenciasDoDia[];
  /**
   * O nome de cada pessoa pelo identificador, para escrever as frases: quem
   * está na escala da casa e quem está escalado em algum dia do mês, inclusive
   * quem já saiu da escala.
   */
  nomePorId: ReadonlyMap<string, string>;
  /** Chamado por "🖨 Imprimir / PDF". */
  onImprimir: () => void;
  /** Se aparecem "Copiar da semana anterior" e "Limpar mês inteiro". */
  podeEditar: boolean;
  /** Chamado por "Copiar da semana anterior". */
  onCopiarSemana: () => void;
  /** Chamado por "Limpar mês inteiro". */
  onLimparMes: () => void;
}

/**
 * O bloco "Ações".
 *
 * @param pendencias - As pendências do mês, na ordem das datas.
 * @param nomePorId - O nome com que cada pessoa aparece nas frases; deverá
 *   trazer também quem saiu da escala e continua escalado num dia, porque quem
 *   não estiver no mapa aparece como `—`.
 * @param onImprimir - Chamado ao pedir a impressão.
 * @param podeEditar - Se aparecem os dois botões que gravam.
 * @param onCopiarSemana - Chamado ao pedir a cópia da semana.
 * @param onLimparMes - Chamado ao pedir a limpeza do mês.
 * @returns A seção com os botões e, quando aberta, a lista de pendências.
 */
export function AcoesDoMes({
  pendencias,
  nomePorId,
  onImprimir,
  podeEditar,
  onCopiarSemana,
  onLimparMes,
}: AcoesDoMesProps) {
  const [conferindo, setConferindo] = useState(false);

  return (
    <SecaoDoPainel titulo="Ações">
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start print:hidden"
          onClick={onImprimir}
        >
          🖨 Imprimir / PDF
        </Button>
        {podeEditar && (
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start"
            onClick={onCopiarSemana}
          >
            Copiar da semana anterior
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          aria-expanded={conferindo}
          className="w-full justify-start"
          onClick={() => setConferindo((atual) => !atual)}
        >
          🔍 Verificar conflitos do mês
        </Button>

        {conferindo && (
          <div
            role="region"
            aria-label="Conflitos do mês"
            className="mt-1.5 max-h-[260px] overflow-y-auto rounded-[4px] border border-aviso bg-aviso/8 px-3 py-2.5 text-xs text-texto-medio"
          >
            {pendencias.length === 0 ? (
              <span className="font-semibold text-sucesso">
                ✅ Nenhum conflito encontrado no mês!
              </span>
            ) : (
              pendencias.map((doDia) => (
                <div key={doDia.dia} className="not-first:mt-2">
                  <p className="font-semibold">📅 {diaPorExtenso(doDia.dia)}</p>
                  {doDia.pendencias.map((pendencia) => (
                    <p
                      key={`${pendencia.colaboradorId}-${pendencia.tipo}`}
                      className={cn('mt-0.5 pl-2.5', pendencia.critico && 'text-perigo')}
                    >
                      • {nomePorId.get(pendencia.colaboradorId) ?? '—'}{' '}
                      {fraseDaPendencia(pendencia, doDia.escalados)}
                    </p>
                  ))}
                </div>
              ))
            )}
          </div>
        )}

        {podeEditar && (
          <Button
            type="button"
            variant="destructive"
            className="w-full justify-start"
            onClick={onLimparMes}
          >
            Limpar mês inteiro
          </Button>
        )}
      </div>
    </SecaoDoPainel>
  );
}
